import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useSWR from 'swr'

import { useAuthToken } from '@/hooks/useAuthToken'
import { ApiError, createFetcher, createUpdater } from '@/services/api'
import { notificationPreferencesService } from '@/services/notificationPreferencesService'
import {
  type CommunicationPreferenceEntry,
  type CommunicationPreferences,
  type OutboundPreferenceChannel,
  parseCommunicationPreferences,
  preferenceKey,
} from '@/types/notificationPreferences'

export type PreferenceSaveState =
  | { readonly state: 'saving' }
  | { readonly state: 'saved' }
  | { readonly state: 'error'; readonly intendedEnabled: boolean }

const SAVED_VISIBLE_MS = 2_500

export type NotificationPreferencesController = {
  readonly preferences: CommunicationPreferences | undefined
  readonly isLoading: boolean
  readonly loadError: Error | undefined
  readonly reload: () => Promise<unknown>
  readonly saveStates: Readonly<Partial<Record<OutboundPreferenceChannel, PreferenceSaveState>>>
  readonly channelEnabled: (channel: OutboundPreferenceChannel) => boolean
  readonly setChannelEnabled: (channel: OutboundPreferenceChannel, enabled: boolean) => void
  readonly retry: (channel: OutboundPreferenceChannel) => void
}

function reconcileServerResponse(
  currentValue: unknown,
  response: CommunicationPreferences,
  requestedChannel: OutboundPreferenceChannel,
  requestGeneration: number,
  revisionSnapshot: Readonly<Record<string, number>>,
  currentRevisions: Readonly<Record<string, number>>,
  appliedGenerations: Record<string, number>,
  channelGeneration: { current: number }
): CommunicationPreferences {
  const current = parseCommunicationPreferences(currentValue) ?? response
  const currentPreferences = new Map(
    current.preferences.map((entry) => [preferenceKey(entry.channel, entry.purpose), entry])
  )
  const preferences = response.preferences.map((entry) => {
    const key = preferenceKey(entry.channel, entry.purpose)
    const hasNewerLocalRevision = (currentRevisions[entry.channel] ?? 0) > (revisionSnapshot[entry.channel] ?? 0)
    const hasNewerServerSnapshot =
      entry.channel !== requestedChannel && (appliedGenerations[key] ?? 0) > requestGeneration
    if (hasNewerLocalRevision || hasNewerServerSnapshot) return currentPreferences.get(key) ?? entry
    appliedGenerations[key] = requestGeneration
    return entry
  })

  const channels = channelGeneration.current > requestGeneration ? current.channels : response.channels
  if (channelGeneration.current <= requestGeneration) channelGeneration.current = requestGeneration

  return { channels, preferences }
}

function declinableEntries(
  preferences: CommunicationPreferences | undefined,
  channel: OutboundPreferenceChannel
): readonly CommunicationPreferenceEntry[] {
  return preferences?.preferences.filter((entry) => entry.channel === channel && entry.declinable) ?? []
}

/**
 * Loads the matrix and saves one channel switch at a time.
 *
 * A channel click writes every declinable purpose of that channel in one
 * request. Repeated clicks on one channel are queued in click order so an
 * older, slower request cannot become the final server value.
 */
export function useNotificationPreferences(enabled: boolean): NotificationPreferencesController {
  const { getToken, isSignedIn } = useAuthToken()
  const url = notificationPreferencesService.getOwnPreferencesUrl()
  const key = enabled && isSignedIn ? url : null
  const fetcher = useMemo(() => (enabled ? createFetcher(getToken) : null), [enabled, getToken])
  const updater = useMemo(() => (enabled ? createUpdater(getToken) : null), [enabled, getToken])
  const {
    data: wireData,
    error,
    isLoading,
    mutate,
  } = useSWR<unknown, ApiError>(key, fetcher, {
    revalidateOnFocus: false,
  })
  const data = useMemo(() => parseCommunicationPreferences(wireData) ?? undefined, [wireData])
  const [optimistic, setOptimistic] = useState<Partial<Record<OutboundPreferenceChannel, boolean>>>({})
  const [saveStates, setSaveStates] = useState<Partial<Record<OutboundPreferenceChannel, PreferenceSaveState>>>({})
  const revisions = useRef<Record<string, number>>({})
  const queues = useRef<Partial<Record<OutboundPreferenceChannel, Promise<void>>>>({})
  const savedTimers = useRef<Partial<Record<OutboundPreferenceChannel, ReturnType<typeof setTimeout>>>>({})
  const requestGeneration = useRef(0)
  const appliedGenerations = useRef<Record<string, number>>({})
  const channelGeneration = useRef({ current: 0 })
  const coordinatorGeneration = useRef(0)
  const enabledRef = useRef(enabled)
  const mounted = useRef(true)

  useEffect(() => {
    if (enabledRef.current !== enabled) {
      enabledRef.current = enabled
      coordinatorGeneration.current += 1
    }
  }, [enabled])

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      for (const timer of Object.values(savedTimers.current)) clearTimeout(timer)
    }
  }, [])

  const enqueueSave = useCallback(
    (channel: OutboundPreferenceChannel, intendedEnabled: boolean) => {
      if (!updater) return
      const purposes = declinableEntries(data, channel).map((entry) => entry.purpose)
      if (purposes.length === 0) return
      const revision = (revisions.current[channel] ?? 0) + 1
      revisions.current[channel] = revision
      requestGeneration.current += 1
      const generation = requestGeneration.current
      const revisionSnapshot = { ...revisions.current }
      const coordinator = coordinatorGeneration.current
      clearTimeout(savedTimers.current[channel])
      setOptimistic((current) => ({ ...current, [channel]: intendedEnabled }))
      setSaveStates((current) => ({ ...current, [channel]: { state: 'saving' } }))

      const prior = queues.current[channel] ?? Promise.resolve()
      const task = prior
        .catch(() => undefined)
        .then(async () => {
          try {
            const wireResponse = await updater<unknown>(url, {
              preferences: purposes.map((purpose) => ({ channel, purpose, enabled: intendedEnabled })),
            })
            const response = parseCommunicationPreferences(wireResponse)
            if (!response) throw new Error('Preference response was malformed')
            if (!mounted.current || coordinatorGeneration.current !== coordinator) return
            await mutate(
              (current: unknown) =>
                reconcileServerResponse(
                  current,
                  response,
                  channel,
                  generation,
                  revisionSnapshot,
                  revisions.current,
                  appliedGenerations.current,
                  channelGeneration.current
                ),
              false
            )
            if (!mounted.current || revisions.current[channel] !== revision) return

            setOptimistic((current) => {
              const next = { ...current }
              delete next[channel]
              return next
            })
            setSaveStates((current) => ({ ...current, [channel]: { state: 'saved' } }))
            savedTimers.current[channel] = setTimeout(() => {
              if (!mounted.current || revisions.current[channel] !== revision) return
              setSaveStates((current) => {
                const next = { ...current }
                delete next[channel]
                return next
              })
            }, SAVED_VISIBLE_MS)
          } catch {
            if (!mounted.current || revisions.current[channel] !== revision) return
            setOptimistic((current) => {
              const next = { ...current }
              delete next[channel]
              return next
            })
            setSaveStates((current) => ({
              ...current,
              [channel]: { state: 'error', intendedEnabled },
            }))
          }
        })
      queues.current[channel] = task
      void task.finally(() => {
        if (queues.current[channel] === task) delete queues.current[channel]
      })
    },
    [data, mutate, updater, url]
  )

  const channelEnabled = useCallback(
    (channel: OutboundPreferenceChannel) =>
      optimistic[channel] ?? declinableEntries(data, channel).some((entry) => entry.enabled),
    [data, optimistic]
  )

  const retry = useCallback(
    (channel: OutboundPreferenceChannel) => {
      const state = saveStates[channel]
      if (state?.state === 'error') enqueueSave(channel, state.intendedEnabled)
    },
    [enqueueSave, saveStates]
  )

  return {
    preferences: data,
    isLoading,
    loadError: error,
    reload: () => mutate(),
    saveStates,
    channelEnabled,
    setChannelEnabled: enqueueSave,
    retry,
  }
}
